import React from 'react'
import {
  fetchBannerData,
  fetchProviderFallbackAffiliateLinksData,
  IEventBannerDataResponse,
  IEventLiveDataResponse,
  IOutLinks,
  IProviderFallbackAffiliateLinksResponse,
  updateLiveEventData
} from '../../api/match-center'
import {
  EventStatus,
  IMaxOdd,
  IWidgetBackground,
  IWidgetProvider,
  oddsFormats,
  widgetBuilderComponentMetaTypes,
  EventTypes
} from '../../utils/types'
import {checkEventType} from "../../utils/competitors";
import {getWidgetBuilderComponentMetaByType} from '../../utils/builder-components'

import LoadingBlock from '../LoadingBlock'
import ComponentWrapper from './ComponentWrapper'
import EventTopBannerFallback from "./EventTopBannerFallback"
import EventTopBannerComponent from "../Sports/EventTopBanner/EventTopBanner"
import EventTopBannerFourCompetitors from "../Sports/EventTopBanner/EventTopBannerFourCompetitors"
import {useGlobalState} from "../../contexts/GlobalState"

interface IProps {
  componentName: string
  eventID: number
  languageID: number
  allowedProviders: null|IWidgetProvider[]
  outLinks: IOutLinks
  oddsFormat: oddsFormats
  isMobile: boolean
  showLinks: boolean
  widgetId: number
  backgrounds: null|IWidgetBackground[]
  builderComponentId: number
}

const EventTopBanner: React.FC<IProps> = (props) => {
  const {
    eventID,
    languageID,
    allowedProviders,
    outLinks,
    oddsFormat,
    isMobile,
    showLinks,
    backgrounds,
    widgetId,
    builderComponentId
  } = props
  const [loading, setLoading] = React.useState<Boolean>(true)
  const [requestFailed, setRequestFailed] = React.useState<Boolean>(false)
  const [eventBannerData, setEventBannerData] = React.useState<IEventBannerDataResponse | null>(null)
  const [providerResponse, setProviderResponse] = React.useState<IProviderFallbackAffiliateLinksResponse | null>(null)
  const {globalState} = useGlobalState()

  const getProviderIds = React.useCallback(() => {
    const outcomeType1x2ProviderId: string | null = getWidgetBuilderComponentMetaByType(widgetBuilderComponentMetaTypes.eventTopBanner1x2ProviderId)
    return outcomeType1x2ProviderId ? [outcomeType1x2ProviderId] : allowedProviders?.map(provider => provider.providerId)
  }, [ allowedProviders ])

  // Do not join these two api calls. Joining of these two created many unnecessary update live data event calls
  // New Fetch banner data and Update live event data
  React.useEffect(() => {
    let isMounted = true;
    const bannerData = async () => {
      try {
        await fetchBannerData({
          widgetId: widgetId,
          languageId: languageID,
          eventId: eventID,
          allowedProvidersIds: getProviderIds(),
        }).then((response: IEventBannerDataResponse) => {
          setEventBannerData(response)
          let oldOddsData: IMaxOdd[] = [];
          const updateEventData = async (initialResponse: IEventBannerDataResponse) => {
            // if no oldOddsData get it from current
            if (!oldOddsData && initialResponse) {
              oldOddsData = initialResponse.maxOdds;
            }
            try {
              // call for new data and await response
              const response = await updateLiveEventData({
                widgetId: widgetId,
                languageId: languageID,
                eventId: eventID,
                allowedProvidersIds: getProviderIds(),
              });

              if (response) {
                let newData: IEventLiveDataResponse = response;
                // check for odds changes
                oldOddsData?.forEach((old) => {
                  //for each of the old odds data use the outcome name to find matching one in the new data
                  const matchingOutcomeIndex = newData?.maxOdds?.findIndex(outcome => outcome.outcomeName === old.outcomeName);

                  // if you can't find a match matchingOutcomeIndex returns -1 , so check if it's found a match
                  if (matchingOutcomeIndex !== -1) {
                    if (newData.maxOdds[matchingOutcomeIndex]?.odds! > old.odds!) {
                      // if odds have increased add this value to the odds data
                      newData.maxOdds[matchingOutcomeIndex].oddsChange = 'drifting';
                    } else if (newData.maxOdds[matchingOutcomeIndex].odds! < old.odds!) {
                      // if odds have decreased add this value to the odds data
                      newData.maxOdds[matchingOutcomeIndex].oddsChange = 'shortening';
                    } else if (newData.maxOdds[matchingOutcomeIndex].odds! === old.odds!) {
                      //if the same use old to avoid wiping any oddsChange info from previous requests
                      newData.maxOdds[matchingOutcomeIndex] = old;
                    }
                  }
                })
                // update banner data
                if (initialResponse) {
                  const updatedData: IEventBannerDataResponse = {...initialResponse};
                  updatedData.gameMoment = newData.gameMoment;
                  updatedData.headerIncidents = newData.headerIncidents;
                  updatedData.maxOdds = newData.maxOdds;
                  updatedData.penalties = newData.penalties;
                  updatedData.periodScores = newData.periodScores;
                  updatedData.score = newData.score;
                  updatedData.status = newData.status;
                  setEventBannerData(updatedData);
                }
                // update the oldOdds data to the new one ready for next check
                oldOddsData = newData.maxOdds;
              }
            } catch (err) {
              // catches errors both in fetch and r
              console.error(err);
              setRequestFailed(true)
            } finally {
              // do it again in 1 minute if match is still in progress
              if (response.status === EventStatus.Inprogress) {
                setTimeout(function () {
                  updateEventData(initialResponse);
                }, 60000)
              }
            }
          };
          if (response.status === EventStatus.Inprogress) {
            updateEventData(response)
          }
        })
      } catch(err) {
        console.log(err)
        setRequestFailed(true)
      } finally {
        setLoading(false)
      }
    }

    if(isMounted) {
      bannerData();
    }

    return () => {
      isMounted = false;
    }
  }, [ getProviderIds, allowedProviders, eventID, languageID, widgetId ])

  // Getting Provider Fallback Affiliate Links Data
  React.useEffect(() => {
    let isMounted = true;
    const getProviderFallbackAffiliateLinksData = async () => {
      try {
        await fetchProviderFallbackAffiliateLinksData({
          providerIds: getProviderIds(),
          widgetId: widgetId,
        }).then((response: IProviderFallbackAffiliateLinksResponse) => {
          setProviderResponse(response)
        })
      } catch(err) {
        console.log(err)
        setRequestFailed(true)
      } finally {
        setLoading(false);
      }
    }

    if(isMounted) {
      getProviderFallbackAffiliateLinksData();
    }

    return () => {
      isMounted = false;
    }
  }, [ getProviderIds, widgetId, allowedProviders, globalState.promotedProvider])

  if(loading) {
    return (
      <LoadingBlock height={365} heightMobile={296} isMobile={isMobile} />
    )
  }

  return (
    <>
      {!requestFailed && providerResponse && eventBannerData && (
        <ComponentWrapper
          children={(
            <>
              {(checkEventType(eventBannerData.eventCompetitors) === EventTypes.event_1v1) && (
                <EventTopBannerComponent
                  eventBannerData={eventBannerData}
                  isMobile={isMobile}
                  backgrounds={backgrounds}
                  widgetId={widgetId}
                  builderComponentId={builderComponentId}
                  outLinkFolder={outLinks.outLinkFolder}
                  oddsFormat={oddsFormat}
                  allowedProviders={allowedProviders}
                  fallbackClientAffiliateLinks={providerResponse.fallbackClientAffiliateLinks}
                  showLinks={showLinks}
                  eventType={checkEventType(eventBannerData?.eventCompetitors)}
                />
              )}
              {(checkEventType(eventBannerData.eventCompetitors) === EventTypes.event_2v2) && (
                <EventTopBannerFourCompetitors
                  eventBannerData={eventBannerData}
                  isMobile={isMobile}
                  backgrounds={backgrounds}
                  widgetId={widgetId}
                  builderComponentId={builderComponentId}
                  outLinkFolder={outLinks.outLinkFolder}
                  oddsFormat={oddsFormat}
                  allowedProviders={allowedProviders}
                  fallbackClientAffiliateLinks={providerResponse.fallbackClientAffiliateLinks}
                  showLinks={showLinks}
                  eventType={checkEventType(eventBannerData?.eventCompetitors)}
                />
              )}
              {((checkEventType(eventBannerData.eventCompetitors) === EventTypes.event_n_v_n) ||
              (checkEventType(eventBannerData.eventCompetitors) === EventTypes.event_unknown)) && (
                <EventTopBannerFallback
                  isMobile={isMobile}
                />
              )}
            </>
          )}
        />
      )}
    </>
  )
}

export default EventTopBanner
